import * as t from "io-ts";
import { fromNewtype } from "io-ts-types/lib/fromNewtype";
import { Newtype, iso } from "newtype-ts";

import { ProductCompareTheme } from "../constants";
import { IProduct } from "./catalogue";
import { ISafeHTML, ProductCategoryID, ProductID, SafeHTML } from "./nominals";
import {
    BackgroundStylesSubBlock,
    ImageChooserBlock,
    PageChooserBlock,
    SpacingSubBlock,
} from "./streamfield-blocks";
import { codecFromEnum, nullable } from "./utils";

export type ITileID = Newtype<{ readonly ITileID: unique symbol }, number>;
const TileID = fromNewtype<ITileID>(t.number);
export const isoTileID = iso<ITileID>();

const AttributeOptionGroup = t.readonly(
    t.type({
        name: t.string,
        options: t.readonlyArray(t.string),
    }),
);
export type IAttributeOptionGroup = t.TypeOf<typeof AttributeOptionGroup>;

const ProductFlair = t.readonly(
    t.partial({
        content: t.union([nullable(t.string), nullable(SafeHTML)]),
        style: nullable(t.string),
    }),
);
export type IProductFlair = t.TypeOf<typeof ProductFlair>;

const ChildFilter = t.readonly(
    t.type({
        option_group: t.readonly(
            t.type({
                id: t.number,
                code: t.string,
            }),
        ),
        behavior: t.string,
        variants: t.readonlyArray(
            t.readonly(
                t.type({
                    id: t.number,
                    name: t.string,
                    group_id: t.number,
                }),
            ),
        ),
    }),
);
export type IChildFilter = t.TypeOf<typeof ChildFilter>;

const ProductTileAttribute = t.readonly(
    t.type({
        description: t.string,
        icon: nullable(ImageChooserBlock),
        name: t.string,
        show_attribute: t.boolean,
    }),
);
export type IProductTileAttribute = t.TypeOf<typeof ProductTileAttribute>;

const ProductCompareRowTypeSlug = t.keyof({
    "richtext": null,
    "attribute-grid": null,
    "attribute-option-list": null,
    "shop-now": null,
    "microconfigurator": null,
    "product-select": null,
    "upgrade": null,
    "dynamic-price": null,
    "price-cta": null,
});

const ProductCompareRowType = t.readonly(
    t.type({
        id: t.number,
        row_type: ProductCompareRowTypeSlug,
        title: t.string,
        group_title: t.union([t.undefined, nullable(t.string)]),
    }),
);
type IProductCompareRowType = t.TypeOf<typeof ProductCompareRowType>;

interface ProductTileAttributeRow {
    readonly row_type: IProductCompareRowType;
    readonly is_highlighted: boolean;
    readonly children: ReadonlyArray<ProductTileAttributeRow>;
    readonly attributes: ReadonlyArray<IProductTileAttribute>;
    readonly richtext: ISafeHTML | null;
    readonly flair: IProductFlair | null;
}

const ProductTileAttributeRow: t.Type<ProductTileAttributeRow> = t.recursion(
    "ProductTileAttributeRow",
    () => {
        const _type = t.readonly(
            t.type({
                row_type: ProductCompareRowType,
                is_highlighted: t.boolean,
                children: t.readonlyArray(ProductTileAttributeRow),
                attributes: t.readonlyArray(ProductTileAttribute),
                richtext: nullable(SafeHTML),
                flair: nullable(ProductFlair),
            }),
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return _type as any;
    },
);
export type IProductTileAttributeRow = t.TypeOf<typeof ProductTileAttributeRow>;

const _BaseCompareTile = <RT extends t.Mixed>(tileType: RT) => {
    return t.readonly(
        t.type({
            flair: nullable(ProductFlair),
            secondary_flair: nullable(ProductFlair),
            product_compare_tile: t.readonly(
                t.intersection([
                    t.type({
                        id: TileID,
                        product_ids: t.readonlyArray(ProductID),
                        name: t.string,
                        title: t.string,
                        short_description: t.string,
                        image: ImageChooserBlock,
                        child_filter: nullable(ChildFilter),
                    }),
                    tileType,
                ]),
            ),
            rows: t.readonlyArray(ProductTileAttributeRow),
            is_promo_period_active: nullable(t.union([t.boolean, t.undefined])),
        }),
    );
};

// IBaseProductCompareTile is the tile as given to us by the CMS
const BaseProductCompareTile = _BaseCompareTile(
    t.readonly(
        t.type({
            product_id: ProductID,
            category_id: t.null,
        }),
    ),
);
const BaseCategoryCompareTile = _BaseCompareTile(
    t.readonly(
        t.type({
            product_id: nullable(ProductID),
            category_id: ProductCategoryID,
        }),
    ),
);
export const BaseCompareTile = t.union([
    BaseProductCompareTile,
    BaseCategoryCompareTile,
]);
export type IBaseCompareTile = t.TypeOf<typeof BaseCompareTile>;

// IProductCompareTile is a IBaseProductCompareTile, but has additional data annotated on it.
export type ICompareTile = IBaseCompareTile & {
    readonly rootProducts: ReadonlyArray<IProduct>;
    readonly childFilterProducts?: ReadonlyArray<IProduct> | null;
};

const GroupColumnsHeaderText = t.readonly(
    t.type({
        alignment: t.string,
        background_style: BackgroundStylesSubBlock,
        content: nullable(SafeHTML),
        full_width: t.boolean,
        spacing: SpacingSubBlock,
    }),
);

const GroupColumnsHeader = t.readonly(
    t.type({
        numColumns: t.string,
        text: GroupColumnsHeaderText,
    }),
);
export type IGroupColumnsHeader = t.TypeOf<typeof GroupColumnsHeader>;

export const ProductSelectionGridCMSData = t.readonly(
    t.type({
        header: SafeHTML,
        product_compare_page: nullable(PageChooserBlock),
        products: t.readonlyArray(BaseCompareTile),
        size_attribute_option_group: nullable(AttributeOptionGroup),
        preselected_size: nullable(t.string),
        theme: t.partial({
            theme: codecFromEnum("ProductCompareTheme", ProductCompareTheme),
        }),
        footer: nullable(SafeHTML),
        group_columns_header: t.union([
            t.readonlyArray(GroupColumnsHeader),
            t.undefined,
        ]),
        show_size_selector: nullable(t.boolean),
    }),
);
export type ProductSelectionGridCMSData = t.TypeOf<
    typeof ProductSelectionGridCMSData
>;

const _AbstractProductCompareGridCMSData = t.readonly(
    t.type({
        header: SafeHTML,
        products: t.readonlyArray(BaseCompareTile),
        size_attribute_option_group: nullable(AttributeOptionGroup),
        preselected_size: nullable(t.string),
    }),
);

export const ProductCompareGridCMSData = t.readonly(
    t.intersection([
        _AbstractProductCompareGridCMSData,
        t.type({
            theme: t.partial({
                theme: codecFromEnum(
                    "ProductCompareTheme",
                    ProductCompareTheme,
                ),
            }),
        }),
    ]),
);
export type ProductCompareGridCMSData = t.TypeOf<
    typeof ProductCompareGridCMSData
>;

export const ProductCompareGridColumnCardCMSData =
    _AbstractProductCompareGridCMSData;
